// TelegramSettings.jsx — Manajemen multi-bot (Bot Profiles)
// 5 bot: solman, sap_usage, billing, cost (query) + notify (notifikasi)
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import useTabDeepLink from '../hooks/useTabDeepLink';
import { api } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';

function webhookBase() {
  if (typeof window !== 'undefined' && window.location) {
    return `${window.location.origin}/api/telegram/webhook`;
  }
  return 'https://your-domain/api/telegram/webhook';
}

// Scope labels are product proper-nouns — same in both languages.
const SCOPE_LABELS = { billing: 'Billing', cost: 'Cost', solman: 'Solman', sap_usage: 'SAP Usage', overtime: 'Overtime', sap_basis: 'SAP Basis', sap_user_mgmt: 'SAP User Mgmt' };

export default function TelegramSettings() {
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const [bots, setBots]        = useState([]);
  const [activeKey, setActive] = useState(() => searchParams.get('tab') || null);
  // Divalidasi terhadap daftar bot yang benar-benar ada. Berbeda dari halaman
  // lain, daftar tab di sini datang dari API (async). Kunjungan PERTAMA tetap
  // aman karena initializer di atas memasang nilai dari URL dan `load()` hanya
  // mengisi default bila masih kosong (`prev || …`). Hook ini menangani
  // kunjungan berikutnya, saat `bots` sudah terisi.
  useTabDeepLink('telegram', (p) => bots.some(b => b.bot_key === p), setActive);
  const [loading, setLoading]  = useState(true);
  const [saving, setSaving]    = useState(false);
  const [msg, setMsg]          = useState(null);
  const [draft, setDraft]      = useState(null);
  const [newToken, setNewToken] = useState('');

  function showMsg(type, text) {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/telegram/bots');
      const list = res.data.bots || [];
      setBots(list);
      setActive(prev => prev || (list.length ? list[0].bot_key : null));
    } catch (err) {
      showMsg('err', t('tg.errLoadBots', { err: err.response?.data?.error || err.message }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!activeKey) return;
    const bot = bots.find(b => b.bot_key === activeKey);
    if (bot) {
      setDraft({
        ...bot,
        allowed_chat_ids: Array.isArray(bot.allowed_chat_ids) ? bot.allowed_chat_ids : [],
        schedules: Array.isArray(bot.schedules) ? bot.schedules : [],
        custom_instructions: bot.tune_config?.custom_instructions || '',
        help_text:           bot.tune_config?.help_text || '',
        welcome_text:        bot.tune_config?.welcome_text || '',
      });
      setNewToken('');
    }
  }, [activeKey, bots]);

  const upd = (field, value) => setDraft(p => ({ ...p, [field]: value }));

  function addChatId() {
    setDraft(p => ({ ...p, allowed_chat_ids: [...(p.allowed_chat_ids || []), { id: '', label: '', min_year: '' }] }));
  }
  function updChatId(idx, field, value) {
    setDraft(p => ({ ...p, allowed_chat_ids: p.allowed_chat_ids.map((e, i) => i === idx ? { ...e, [field]: value } : e) }));
  }
  function rmChatId(idx) {
    setDraft(p => ({ ...p, allowed_chat_ids: p.allowed_chat_ids.filter((_, i) => i !== idx) }));
  }
  function addSchedule() {
    setDraft(p => ({ ...p, schedules: [...(p.schedules || []), { label: '', time: '08:00', days: [1,2,3,4,5], data_type: 'all', enabled: true }] }));
  }
  function updSchedule(idx, field, value) {
    setDraft(p => ({ ...p, schedules: p.schedules.map((s, i) => i === idx ? { ...s, [field]: value } : s) }));
  }
  function toggleDay(idx, day) {
    setDraft(p => ({ ...p, schedules: p.schedules.map((s, i) => {
      if (i !== idx) return s;
      const days = s.days || [];
      return { ...s, days: days.includes(day) ? days.filter(d => d !== day) : [...days, day].sort() };
    }) }));
  }
  function rmSchedule(idx) {
    setDraft(p => ({ ...p, schedules: p.schedules.filter((_, i) => i !== idx) }));
  }

  async function handleSave() {
    if (!draft) return;
    setSaving(true);
    try {
      await api.post(`/api/telegram/bots/${draft.bot_key}`, {
        name:             draft.name,
        bot_token:        newToken || undefined,
        enabled:          draft.enabled,
        allowed_chat_ids: draft.allowed_chat_ids,
        schedules:        draft.schedules,
        ai_key_override:  draft.ai_key_override,
        webhook_url:      `${webhookBase()}/${draft.bot_key}`,
        custom_instructions: draft.custom_instructions ?? '',
        help_text:           draft.help_text ?? '',
        welcome_text:        draft.welcome_text ?? '',
      });
      showMsg('ok', t('tg.savedOk'));
      await load();
    } catch (err) {
      showMsg('err', err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    if (!draft) return;
    try {
      const res = await api.post(`/api/telegram/bots/${draft.bot_key}/test`, { text: t('tg.testText', { name: draft.name }) });
      showMsg('ok', res.data.message);
    } catch (err) {
      showMsg('err', err.response?.data?.error || err.message);
    }
  }

  if (loading) return <div className="p-6 text-gray-400 text-sm animate-pulse">{t('tg.loadingBots')}</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <span className="text-3xl">✈️</span>
        <div>
          <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">{t('tg.title')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('tg.subtitle')}</p>
        </div>
      </div>

      {msg && (
        <div className={`px-4 py-2 rounded-lg text-sm ${msg.type === 'ok' ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'}`}>
          {msg.text}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {bots.map(b => (
          <button key={b.bot_key} onClick={() => setActive(b.bot_key)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              activeKey === b.bot_key ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-blue-400'
            }`}>
            <span className={`w-2 h-2 rounded-full ${b.enabled && b.bot_token_set ? 'bg-green-400' : 'bg-gray-300'}`} />
            {b.name}
            {b.bot_type === 'notify' && <span className="text-xs opacity-70">🔔</span>}
          </button>
        ))}
      </div>

      {draft && (
        <>
          <Section title={t('tg.sectionConfig', { name: draft.name })}>
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="px-2 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                {t('tg.typeLabel')} {draft.bot_type === 'notify' ? t('tg.typeNotify') : t('tg.typeQuery')}
              </span>
              {(draft.scope || []).map(s => (
                <span key={s} className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  {SCOPE_LABELS[s] || s}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between py-2 mb-3">
              <div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('tg.enableBot')}</div>
                <div className="text-xs text-gray-400">{t('tg.enableBotHint')}</div>
              </div>
              <button onClick={() => upd('enabled', draft.enabled ? 0 : 1)}
                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${draft.enabled ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${draft.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('tg.botName')}</label>
              <input type="text" value={draft.name || ''} onChange={e => upd('name', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('tg.botToken')} {draft.bot_token_set && <span className="text-green-500 text-xs">{t('tg.tokenSet')}</span>}
              </label>
              <input type="text" value={newToken} onChange={e => setNewToken(e.target.value)}
                placeholder={draft.bot_token_set ? t('tg.tokenPlaceholderSet') : t('tg.tokenPlaceholder')}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <p className="text-xs text-gray-400 mt-1">{t('tg.tokenHint')}</p>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('tg.webhookUrl')}</label>
              <code className="block px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700/40 text-xs text-gray-600 dark:text-gray-400 break-all">
                {webhookBase()}/{draft.bot_key}
              </code>
              <p className="text-xs text-gray-400 mt-1">{t('tg.webhookHint')}</p>
            </div>

            <div className="flex gap-2">
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded-lg font-medium transition-colors">
                {saving ? t('tg.saving') : t('tg.save')}
              </button>
              <button onClick={handleTest}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg font-medium transition-colors">
                {t('tg.testSend')}
              </button>
            </div>
          </Section>

          <Section title={t('tg.sectionChatIds')}>
            <p className="text-xs text-gray-400 mb-3">
              {t('tg.chatIdsHint', { scope: (draft.scope || []).map(s => SCOPE_LABELS[s] || s).join(', ') })}
            </p>
            <div className="space-y-2">
              {(draft.allowed_chat_ids || []).map((entry, idx) => (
                <div key={idx} className="flex gap-2 items-center flex-wrap">
                  <input type="text" value={entry.label || ''} onChange={e => updChatId(idx, 'label', e.target.value)}
                    placeholder={t('tg.phName')} className="w-32 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <input type="text" value={entry.id || ''} onChange={e => updChatId(idx, 'id', e.target.value)}
                    placeholder={t('tg.phChatId')} className="flex-1 min-w-[140px] px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <input type="text" value={entry.min_year || ''} onChange={e => updChatId(idx, 'min_year', e.target.value.replace(/\D/g, '').slice(0,4))}
                    placeholder={t('tg.phMinYear')} className="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <button onClick={() => rmChatId(idx)} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-lg">×</button>
                </div>
              ))}
            </div>
            <button onClick={addChatId} className="mt-2 w-full py-2 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-600 text-sm text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors">
              {t('tg.addChatId')}
            </button>
          </Section>

          {draft.bot_type !== 'notify' && (
            <Section title={t('tg.sectionAnswers')}>
              <p className="text-xs text-gray-400 mb-3">
                {t('tg.answersHint')}
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('tg.aiInstr')}
                  </label>
                  <textarea
                    value={draft.custom_instructions || ''}
                    onChange={e => upd('custom_instructions', e.target.value)}
                    rows={4}
                    placeholder={t('tg.aiInstrPlaceholder')}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                  />
                  <p className="text-xs text-gray-400 mt-1">{t('tg.aiInstrHint')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('tg.welcome')}
                  </label>
                  <textarea
                    value={draft.welcome_text || ''}
                    onChange={e => upd('welcome_text', e.target.value)}
                    rows={2}
                    placeholder={t('tg.welcomePlaceholder')}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('tg.help')}
                  </label>
                  <textarea
                    value={draft.help_text || ''}
                    onChange={e => upd('help_text', e.target.value)}
                    rows={3}
                    placeholder={t('tg.helpPlaceholder')}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                  />
                </div>
              </div>
            </Section>
          )}

          <Section title={t('tg.sectionSchedule')}>
            <p className="text-xs text-gray-400 mb-3">
              {draft.bot_type === 'notify'
                ? t('tg.scheduleHintNotify')
                : t('tg.scheduleHintQuery')}
            </p>
            <div className="space-y-3">
              {(draft.schedules || []).map((s, idx) => (
                <ScheduleCard key={idx} sched={s} scope={draft.scope} t={t}
                  onUpdate={(f, v) => updSchedule(idx, f, v)}
                  onToggleDay={(d) => toggleDay(idx, d)}
                  onRemove={() => rmSchedule(idx)} />
              ))}
            </div>
            <button onClick={addSchedule} className="mt-2 w-full py-2 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-600 text-sm text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors">
              {t('tg.addSchedule')}
            </button>
            <button onClick={handleSave} disabled={saving}
              className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded-lg font-medium transition-colors">
              {saving ? t('tg.saving') : t('tg.saveSchedule')}
            </button>
          </Section>

          <Section title={t('tg.sectionGuide')}>
            <ol className="space-y-2 text-sm text-gray-600 dark:text-gray-400 list-decimal list-inside">
              <li>{t('tg.guide1')}</li>
              <li>{t('tg.guide2')}</li>
              <li>{t('tg.guide3')}</li>
              <li>{t('tg.guide4')}</li>
              <li>{t('tg.guide5')}</li>
            </ol>
          </Section>
        </>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">{title}</h2>
      {children}
    </div>
  );
}

function ScheduleCard({ sched, scope, t, onUpdate, onToggleDay, onRemove }) {
  const dow      = [t('tg.dow0'), t('tg.dow1'), t('tg.dow2'), t('tg.dow3'), t('tg.dow4'), t('tg.dow5'), t('tg.dow6')];
  const typeOpts = ['all', ...(scope || [])];
  const typeLabel = { all: t('tg.typeAll'), billing: 'Billing', cost: 'Cost', solman: 'Solman', sap_usage: 'SAP Usage', overtime: 'Overtime', sap_basis: 'SAP Basis', sap_user_mgmt: 'SAP User Mgmt' };
  return (
    <div className={`p-4 rounded-lg border ${sched.enabled ? 'border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10' : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30'}`}>
      <div className="flex gap-2 items-center mb-2">
        <input type="text" value={sched.label || ''} onChange={e => onUpdate('label', e.target.value)}
          placeholder={t('tg.phScheduleLabel')} className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <input type="time" value={sched.time || '08:00'} onChange={e => onUpdate('time', e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <button onClick={() => onUpdate('enabled', !sched.enabled)}
          className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${sched.enabled ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
          <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${sched.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
        <button onClick={onRemove} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-lg">×</button>
      </div>
      <div className="flex gap-1 mb-2 flex-wrap">
        {dow.map((d, i) => (
          <button key={i} onClick={() => onToggleDay(i)}
            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${(sched.days || []).includes(i) ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'}`}>
            {d}
          </button>
        ))}
      </div>
      <select value={sched.data_type || 'all'} onChange={e => onUpdate('data_type', e.target.value)}
        className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500">
        {typeOpts.map(opt => <option key={opt} value={opt}>{typeLabel[opt] || opt}</option>)}
      </select>
    </div>
  );
}
